import { useEffect, useState } from 'react';
import { supabaseService } from '@/shared/services/supabaseService';

export interface UserStats {
  totalClients: number;
  totalQuotes: number;
  totalInvoices: number;
  totalRevenue: number;
  totalPayments: number;
}

/**
 * Hook to fetch user statistics from the database
 * Uses Supabase to query actual data for clients, quotes, invoices, and revenue
 */
export const useUserStats = (userId: string | undefined) => {
  const [stats, setStats] = useState<UserStats>({
    totalClients: 0,
    totalQuotes: 0,
    totalInvoices: 0,
    totalRevenue: 0,
    totalPayments: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const client = supabaseService.client;

        // Fetch total clients count
        const { count: clientCount, error: clientError } = await client
          .from('clients')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId);

        if (clientError) throw clientError;

        // Fetch total quotes count
        const { count: quoteCount, error: quoteError } = await client
          .from('quotes')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId);

        if (quoteError) throw quoteError;

        // Fetch total invoices count and sum of total amounts
        const { data: invoices, error: invoiceError } = await client
          .from('invoices')
          .select('total_amount')
          .eq('user_id', userId);

        if (invoiceError) throw invoiceError;

        const invoiceCount = invoices?.length || 0;
        const totalRevenue = invoices?.reduce((sum, inv) => sum + (parseFloat(inv.total_amount) || 0), 0) || 0;

        // Fetch total payments count and sum of paid amounts
        const { data: payments, error: paymentsError } = await client
          .from('payments')
          .select('amount')
          .eq('user_id', userId)
          .eq('status', 'completed');

        if (paymentsError) throw paymentsError;

        const paymentCount = payments?.length || 0;
        const totalPaymentsAmount = payments?.reduce((sum, pay) => sum + (parseFloat(pay.amount) || 0), 0) || 0;

        setStats({
          totalClients: clientCount || 0,
          totalQuotes: quoteCount || 0,
          totalInvoices: invoiceCount,
          totalRevenue: totalRevenue,
          totalPayments: totalPaymentsAmount,
        });
      } catch (err) {
        console.error('Error fetching user stats:', err);
        setError(err as Error);
        // Set default values on error
        setStats({
          totalClients: 0,
          totalQuotes: 0,
          totalInvoices: 0,
          totalRevenue: 0,
          totalPayments: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [userId]);

  return { stats, loading, error };
};
